import { Context } from "../Engine";
import { PropertyAnimation } from "../../Common/Animation/PropertyAnimation";
import { Camera } from "../View/Camera";

export class ObjectManager {

    private renderModeObjects: Map<RenderMode, IDrawableObject[]>;
    private objects: Map<IDrawableObject, RenderMode[]>;

    constructor() {
        this.renderModeObjects = new Map();
        this.objects = new Map();
    }

    private getObjects(mode: RenderMode): IDrawableObject[] {
        if (!this.renderModeObjects.has(mode)) {
            this.renderModeObjects.set(mode, []);
        }
        return this.renderModeObjects.get(mode);
    }

    public addObject(obj: IDrawableObject, ...renderModes: RenderMode[]) {
        if (this.objects.has(obj)) {
            return;
        }
        this.objects.set(obj, renderModes);

        for (let mode of renderModes) {
            let list = this.getObjects(mode);
            list.push(obj);

            list.sort((a, b) => a.order - b.order);
        }
    }

    public removeObject(obj: IDrawableObject) {
        if(!this.objects.has(obj)) {
            return;
        }

        for(let mode of this.objects.get(obj)) {
            let list = this.getObjects(mode);
            let idx = list.indexOf(obj);

            list.splice(idx, 1);
        }

        this.objects.delete(obj);
    }

    public iterate(mode: RenderMode, callback: (obj: IDrawableObject) => void) {
        this.getObjects(mode).forEach(callback);
    }
}

export enum RenderMode {
    Primary,
    Picking,
    Occlusion
}

export type RenderSettings = {
    mode: RenderMode,
    pickingReference?: number
}

export interface IDrawableObject {
    order: number;
    render(ctx: Context, camera: Camera, settings: RenderSettings): void;

    animatedProperties: PropertyAnimation;
    interaction: ObjectInteraction;
}

export class CompositeObjectInteraction implements ObjectInteraction {

    private interactors: Set<ObjectInteraction>;

    constructor() {
        this.interactors = new Set<ObjectInteraction>();
    }

    public addInteractor(interactor: ObjectInteraction) {
        this.interactors.add(interactor);
    }

    public removeInteractor(interactor: ObjectInteraction) {
        this.interactors.delete(interactor);
    }

    public onPressDown() {
        this.invokeInteractor('onPressDown');
    }

    public onPressRelease() {
        this.invokeInteractor('onPressRelease');
    }

    public onHoverIn() {
        this.invokeInteractor('onHoverIn');
    }

    public onHoverOut() {
        this.invokeInteractor('onHoverOut');
    }

    private invokeInteractor(type: string) {
        this.interactors.forEach(interactor => {
            if (interactor[type]) {
                interactor[type]();
            }
        });
    }
}

export type ObjectInteraction = {
    onPressDown?: () => void,
    onPressRelease?: () => void,
    onHoverIn?: () => void,
    onHoverOut?: () => void
}